import { useInfinityGetProcedures } from '@vindr/services/actions/procedure.api';
import { ParamsGetProcedure } from '@vindr/services/apis/procedure.api';
import {
  addToArray,
  filterValidValue,
  handlePopupScroll,
  uniqueObjectArray,
} from '@vindr/utils/helpers/common.helper';
import { useCallback, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import EditOrderForm from './EditOrderForm';
import { FormItem } from '@vindr/ui';

const ProcedureName = ({
  orderData,
}: {
  orderData: Parameters<typeof EditOrderForm>['0']['orderData'];
}) => {
  const { t } = useTranslation('Vindoc');

  const [searchProcedures, setSearchProcedures] = useState('');
  const [isPending, startTransition] = useTransition();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading, isFetching } =
    useInfinityGetProcedures({
      limit: searchProcedures ? 25 : 100,
      offset: 0,
      active: true,
      institution_ids: addToArray(orderData?.institution_id),
      ...filterValidValue<ParamsGetProcedure>(
        { name: searchProcedures },
        { invalidList: [null, undefined, ''] }
      ),
    });

  const handleSearch = useCallback((searchText) => {
    startTransition(() => {
      setSearchProcedures(searchText);
    });
  }, []);

  const procedureList = useMemo(() => {
    const listData = (data?.pages?.map((x) => x.data) || []).flat();
    const selectedProcedure = { ...orderData?.procedure, id: orderData?.procedure_id };

    return uniqueObjectArray([selectedProcedure, ...listData], 'id', 'all')?.map((it) => ({
      label: it?.name || it?.code,
      value: it?.id,
    }));
  }, [data?.pages, orderData?.procedure, orderData?.procedure_id]);

  const onPopupScroll = useCallback(
    (e) => handlePopupScroll(e, hasNextPage, fetchNextPage, isFetching),
    [fetchNextPage, hasNextPage, isFetching]
  );

  return (
    <FormItem.Select
      name="procedure_id"
      label={t('Procedure Name')}
      placeholder={t('Select Procedure Name')}
      wrapperClassName="mb-2"
      className="mb-0"
      showSearch
      searchVietnamese
      options={procedureList}
      onPopupScroll={onPopupScroll}
      loading={isInitialLoading || isFetchingNextPage || isPending}
      onSearch={handleSearch}
      required
      allowClear={false}
    />
  );
};

export default ProcedureName;
